import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import {
  appServiceAddNewObjectType,
  appServiceRawObjectTypes,
} from 'slices/appService.slice';
import { useAppDispatch } from 'slices/store';
import type { TAppName } from 'types/app.type';
import CONSTANTS from '@timeedit/types/lib/constants';
import { EFieldMappingModes } from '@timeedit/types/lib/enums';
import {
  Button,
  Col,
  Collapse,
  Form,
  Input,
  Row,
  Select,
} from '@timeedit/ui-components/antd';

type TAddNewObjectType = {
  appName: TAppName;
  allowedMappingModes?: any;
  objectTypes?: any;
  rawObjectTypesLoading?: any;
};
const AddNewObjectType = ({
  appName,
  allowedMappingModes = ['FREE_MAPPING'],
  objectTypes,
  rawObjectTypesLoading,
}: TAddNewObjectType) => {
  // State var to hold the new object type
  const dispatch = useAppDispatch();
  const rawObjectTypes = useSelector(appServiceRawObjectTypes(appName));
  const [objectType, setObjectType] = useState<any>({});
  // Callback for creating the new object type
  const onAddNewObjectType = useCallback(() => {
    dispatch(appServiceAddNewObjectType({ appName, objectType, objectTypes }));
  }, [appName, objectType]);

  return (
    <Collapse defaultActiveKey={[]}>
      <Collapse.Panel header="Add new object type" key="1">
        <Row justify="space-between">
          <Col span={5}>
            <Form layout="vertical">
              <Form.Item label="Label">
                <Input
                  size="small"
                  value={objectType.applicationObjectTypeLabel}
                  onChange={(e) =>
                    setObjectType({
                      ...objectType,
                      applicationObjectTypeLabel: e.target.value,
                    })
                  }
                />
              </Form.Item>
            </Form>
          </Col>
          <Col span={5}>
            <Form layout="vertical">
              <Form.Item label="Property name">
                <Input
                  size="small"
                  disabled
                  value={objectType.objectTypeExtId}
                />
              </Form.Item>
            </Form>
          </Col>
          <Col span={5}>
            <Form layout="vertical">
              <Form.Item label="Mapping type" className="data-model">
                <Select
                  size="small"
                  value={objectType.fieldMappingMode || undefined}
                  style={{ minWidth: '100%' }}
                  onChange={(fieldMappingMode) =>
                    setObjectType({ ...objectType, fieldMappingMode })
                  }
                >
                  {Object.keys(EFieldMappingModes)
                    .filter((el) => allowedMappingModes.indexOf(el) > -1)
                    .map((el: string) => (
                      <Select.Option key={el} value={el}>
                        {get(CONSTANTS, `FieldMappingModeProps.${el}.label`)}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Form>
          </Col>
          <Col span={5}>
            <Form layout="vertical">
              <Form.Item label="Mapped to object type" className="data-model">
                <Select
                  size="small"
                  placeholder="No object type selected"
                  value={objectType.objectTypeExtId || undefined}
                  style={{ minWidth: '100%' }}
                  onChange={(objectTypeExtId) =>
                    setObjectType({
                      ...objectType,
                      objectTypeExtId,
                      objectTypeId: rawObjectTypes.find(
                        (type: any) => type.extid === objectTypeExtId
                      )?.id,
                      applicationObjectTypeName: objectTypeExtId,
                    })
                  }
                  loading={rawObjectTypesLoading}
                  disabled={rawObjectTypesLoading}
                  showSearch
                  filterOption={(query, item: any) =>
                    !query ||
                    `${item.props.value}${item.props.children}`
                      .toLowerCase()
                      .indexOf(query.toLowerCase()) > -1
                  }
                >
                  {rawObjectTypes &&
                    rawObjectTypes.map((item: any) => (
                      <Select.Option value={item.extid} key={item.extid}>
                        {item.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Form>
          </Col>
        </Row>
        <Row>
          <Col span={6}>
            <Button
              size="small"
              type="primary"
              onClick={onAddNewObjectType}
              disabled={
                !objectType.applicationObjectTypeLabel ||
                !objectType.objectTypeExtId
              }
            >
              Add object type
            </Button>
          </Col>
        </Row>
      </Collapse.Panel>
    </Collapse>
  );
};

export default AddNewObjectType;
